import { Component, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngxs/store';
import { TableModule } from 'primeng/table'
import { ReferralState } from '../../../state/referrals/referral-state';
import { AsyncPipe, DatePipe, NgIf } from '@angular/common';
import { ReferralClosureReason, TealCompReferral, TealCompReferralHeader, TealCompReferralUpdate } from '../../../models/teal-comp-referral';
import { CardModule } from 'primeng/card'
import { lastValueFrom, map, Observable } from 'rxjs';
import { ReferralEditorComponent } from '../referral-editor/referral-editor.component';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Referrals } from '../../../state/referrals/referral-actions';
import { TabViewModule } from 'primeng/tabview';
import { TealCompEncounter, TealCompEncounterDetails, TealCompEncounterHeader } from '../../../models/encounter';
import { BlockUIModule } from 'primeng/blockui';
import { Encounters } from '../../../state/encounters/encounter-actions';
import { UserInterfaceState } from '../../../state/ui/ui-state';
import { DropdownModule } from 'primeng/dropdown';
import { ReferralService } from '../../../services/referral.service';
import { EncounterService } from '../../../services/encounter.service';
import { ProviderLocationIdentifier } from '@harlowe/core';
import { ClaimService } from '../../../services/claim.service';
import { Adjuster } from '../../../models/adjuster';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { InplaceModule } from 'primeng/inplace';
import { TooltipModule } from 'primeng/tooltip';
import { BlockUI } from '@harlowe/core';
import { BadgeModule } from "primeng/badge";
import { PanelModule } from 'primeng/panel';

@Component({
  selector: 'app-referral-list',
  standalone: true,
  imports: [TableModule, AsyncPipe, NgIf, CardModule,
    ReferralEditorComponent, OverlayPanelModule, ButtonModule,
    DialogModule, AsyncPipe, TabViewModule, BlockUIModule,
    DatePipe, DropdownModule, FormsModule, InplaceModule, TooltipModule, BadgeModule, PanelModule],
  templateUrl: './referral-list.component.html',
  styleUrl: './referral-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReferralListComponent 
{
  //@Output() saveDraftEmail: EventEmitter<IDraftEmail> = new EventEmitter<IDraftEmail>();
  
  newReferrals$ : Observable<TealCompReferralHeader[]> = this._store.select(ReferralState.newReferrals);
  reviewedReferrals$ : Observable<TealCompReferralHeader[]> = this._store.select(ReferralState.reviewedReferrals);
  
  //For reviewed referrals, we need full copies. So, every time it changes, we'll need to go out and get 'em
  reviewedReferrals : TealCompReferral[] = [];

  unopenedReferralIds$ : Observable<number[]> = this._store.select(ReferralState.unopenedReferralIds);
  unopenedReferralIds: number[] = [];

  submittedForApprovalReferrals$ : Observable<TealCompReferralHeader[]> = this._store.select(ReferralState.submittedForApprovalReferrals);
  selectedReferral$ : Observable<TealCompReferral | undefined> = this._store.select(ReferralState.currentReferral);
  selectedReferralEncounter$ : Observable<TealCompEncounterDetails | undefined> = this._store.select(ReferralState.currentReferralEncounter);
  closureReasons$ : Observable<ReferralClosureReason[] | undefined> = this._store.select(UserInterfaceState.referralClosureReasons);
  authorizingReferrals$: Observable<number[]> = this._store.select(ReferralState.referralIdsAuthorizing);
  creatingReferrals$: Observable<number[]> = this._store.select(ReferralState.referralIdsCreating);

  recentlyClosedReferrals$: Observable<TealCompReferralHeader[]> = this._store.select(ReferralState.recentlyClosedReferrals);

  referrals$: Observable<TealCompReferralHeader[]> =  this._store.select(ReferralState.referrals);
  referrals: TealCompReferralHeader[] = [];

  closureReasonMap$: Observable<Record<number, string>> = new Observable();

  closureReason?: ReferralClosureReason
  
  visibility: boolean = false;
  waiting:boolean = false;
  dropdownOptions: { [rowId: number]: Adjuster[] } = {};
  selectedOptions: { [rowId: number]: Adjuster } = {};
  displayPdfDialog = false;
  currentPdfUrl: SafeResourceUrl | null = null;
  blobUrl: string | null = null;

  constructor
  (
    private _store: Store, 
    private _encounterService: EncounterService,
    private _referralService: ReferralService,
    private _claimService: ClaimService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  )
  {
    this.referrals$.subscribe
    (
      referrals => 
      {  
        this.referrals = referrals
      }
    );


    this.reviewedReferrals$.subscribe
    (
      async referrals =>
      {
        this.reviewedReferrals = await this._referralService.getReferrals(referrals.map(r => r.id));
      }
    )

    this.unopenedReferralIds$.subscribe
    (
      referralIds =>
      {
        this.unopenedReferralIds = referralIds;
        this.cdr.markForCheck();
      }
    )


    this.closureReasonMap$ = this.closureReasons$.pipe(
      map(reasons =>
        (reasons ?? []).reduce((acc, r) => {
          acc[r.id] = r.description;
          return acc;
        }, {} as Record<number, string>)
      )
    );

  }

  referralSelected(referral: TealCompReferralHeader)
  {
    this.visibility = true;
    this.waiting = true;
    lastValueFrom(this._store.dispatch(new Referrals.ReferralSelected({referral:referral}))).then(v => {
      this.waiting = false;
    });
  }

  referralDialogClosed()
  {
    this._store.dispatch(new Referrals.ReferralUpdateComplete());
  }

  async referralUpdated(referral: TealCompReferral)
  {
    //this._store.dispatch(new Referrals.ReferralUpdated({referral:referral}));
    this.visibility=false;
    // TODO: Update encounter list
    let newRef = referral;
    let current = this.referrals.find(r => r.id == newRef.id);
    let ru = new TealCompReferralUpdate(newRef.id);
    //Set fields based on what is different
    var fieldChanged = false; 
    if(newRef.status != current?.status)
    {
      ru.status = newRef.status;
      fieldChanged = true;
    }
    if(newRef.approvedDate != current?.approvedDate)
    {
      ru.approvedDate = newRef.approvedDate;
      fieldChanged = true;
    }
    // if(newRef.submitterIsAdjuster != current?.submitterIsAdjuster)
    //     ru.submitterIsAdjuster = newRef.submitterIsAdjuster;
    // if(newRef.submitterIsReferringProvider != current?.submitterIsReferringProvider)
    //     ru.submitterIsReferringProvider = newRef.submitterIsReferringProvider;
    if(newRef.submitterJobFunction != current?.submitterJobFunction)
    {
      ru.submitterJobFunction = newRef.submitterJobFunction;
      fieldChanged = true;
    }
    if(newRef.submitterName != current?.submitterName)
    {
      ru.submitterName = newRef.submitterName;
      fieldChanged = true;
    }
    if(newRef.submitterPhone != current?.submitterPhone)
    {
      ru.submitterPhone = newRef.submitterPhone;
      fieldChanged = true;
    }
    if(newRef.submitterEmail != current?.submitterEmail)
    {
      ru.submitterEmail = newRef.submitterEmail;
      fieldChanged = true;
    }
    if(newRef.claim?.id != current?.claimId)
    {
      ru.claimId = newRef.claim?.id;
      fieldChanged = true;
    }
    if(newRef.referringProvider != undefined && newRef.referringProvider?.facility.ein != current?.referringProviderId?.ein)
    {
      ru.referringProviderId = new ProviderLocationIdentifier(newRef.referringProvider.facility.ein, newRef.referringProvider?.facility.npi, newRef.referringProvider.location.id)
      fieldChanged = true;
    }
    if(newRef.servicingProvider != undefined && newRef.servicingProvider?.facility.ein != current?.servicingProviderId?.ein)
    {
      ru.servicingProviderId = new ProviderLocationIdentifier(newRef.servicingProvider.facility.ein, newRef.servicingProvider?.facility.npi, newRef.servicingProvider.location.id)
      fieldChanged = true;
    }
    if(JSON.stringify(newRef.procedures.map(p => p.id)) != JSON.stringify(current?.procedureIds))
    {
      ru.procedures = newRef.procedures.map(p => p.id);
      fieldChanged = true;
    }
    if(newRef.specialInstructions != current?.specialInstructions)
    {
      ru.specialInstructions = newRef.specialInstructions;
      fieldChanged = true;
    }
    
    if (fieldChanged) {
      this._referralService.updateReferral(newRef.id,ru)
      this.updateEncounter(referral.encounterId);
    }
  }

  async sendReferralForApproval(referral: TealCompReferral)
  {
    
    this._store.dispatch(new Referrals.ReferralSubmittedForAuthorization({referral}));

    var adjuster: Adjuster | undefined = this.selectedOptions[referral.claim?.id as number]
    var auth = await this._referralService.submitForAuthorization(referral.id) as TealCompReferralHeader;

    if (auth)
      this._store.dispatch(new Referrals.ReferralAuthorizationStatus({referral:auth}));

    // TODO: Update encounter list
    this.updateEncounter(referral.encounterId);
  }

  async reSendReferralForApproval(referral: TealCompReferral)
  {
    this._store.dispatch(new Referrals.ReferralSubmittedForReAuthorization({referral}));
    
    var adjuster: Adjuster | undefined = this.selectedOptions[referral.claim?.id as number];
    var auth = await this._referralService.resubmitForAuthorization(referral.id) as TealCompReferralHeader;

    if (auth)
      this._store.dispatch(new Referrals.ReferralAuthorizationStatus({referral:auth}));

    // TODO: Update encounter list
    this.updateEncounter(referral.encounterId);
  }

  openEncounter(referral: TealCompReferral )
  {
    this._store.dispatch(new Encounters.EncounterOpenedFromReferralManager({encounterId:referral.encounterId}));
  }

  async placeInReview(referral: TealCompReferral)
  {
    // var ru = new TealCompReferralUpdate(referral.id);
    // ru.status = "New";
    // await this._referrals.updateReferral(ru.referralId,ru);
    this._store.dispatch(new BlockUI.BlockUI());

    var updatedReferral = await this._referralService.placeReferralBackInReview(referral.id);
    // TODO: Update encounter list
    if (updatedReferral)
      this.updateEncounter(referral.encounterId);
    await this._store.dispatch(new BlockUI.UnBlockUI());



  }

  async updateEncounter(encounterId: number)
  {
    var encounter: TealCompEncounter = await lastValueFrom(this._encounterService.getEncounter(encounterId));
    var header: TealCompEncounterHeader = await lastValueFrom(await this._encounterService.getEncounterHeader(encounterId));
    this._store.dispatch(new Encounters.EncounterUpdated({header: header, encounter: encounter}));
  }

  async closeReferral(referral: TealCompReferral, reason: ReferralClosureReason | undefined)
  {
    console.log(reason);
    if(reason) {
      var closedReferral = await this._referralService.closeReferral(referral.id,reason.id);
      if (closedReferral)
          this.updateEncounter(referral.encounterId);
    }
  }

  async loadClaim(claimId: number)
  {
    if (this.dropdownOptions[claimId])
      return;

    var claim = await lastValueFrom(this._claimService.getClaim(claimId));
    this.dropdownOptions[claimId] = [];
    if (claim.adjuster) 
      this.dropdownOptions[claimId] = [...this.dropdownOptions[claimId], claim.adjuster];
    if (claim.adjuster2)
      this.dropdownOptions[claimId] = [...this.dropdownOptions[claimId], claim.adjuster2];
    if (claim.adjusterSupervisor)
      this.dropdownOptions[claimId] = [...this.dropdownOptions[claimId], claim.adjusterSupervisor];
  }

  async createContractPdf(referral: TealCompReferral)
  {
    this._store.dispatch(new Referrals.ReferralAuthSubmittedForCreation({referral}));

    var r = await this._referralService.createContract(referral.id);    

    this._store.dispatch(new Referrals.ReferralAuthCreated({referral}));


  }

  async viewContractPdf(referral: TealCompReferralHeader)
  {
    this._store.dispatch(BlockUI.BlockUI);
    var blob = await this._referralService.getContractPDF(referral.id);

    const newBlobUrl = URL.createObjectURL(blob);

    if (this.blobUrl)
      URL.revokeObjectURL(this.blobUrl);

    this.currentPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(newBlobUrl);
    this.displayPdfDialog = true; 
    this._store.dispatch(BlockUI.UnBlockUI);

  }

  onDialogHide()
  {
    if (this.blobUrl)
    {
      URL.revokeObjectURL(this.blobUrl);
      this.currentPdfUrl = null;
    }
  }

  ngOnDestroy()
  {
    if (this.blobUrl)
      URL.revokeObjectURL(this.blobUrl);
  }
  // saveDraft(draftEmail: IDraftEmail)
  // {
  //   this.saveDraftEmail.emit(draftEmail);
  // }

  async contractSignerChanged(referral: TealCompReferral)
  {
    //Update the signer
    var referralUpdate = new TealCompReferralUpdate(referral.id);
    referralUpdate.selectedAdjusterId = referral.selectedContractApprover?.id;

    await this._referralService.updateReferral(referral.id,referralUpdate);
  }

}
